import {
  IndividualStationFilterOption,
  OptionType,
} from '@app/components/stations/advanced-filter';
import { useToast } from '@app/core/notifications';
import { useStationsWithAudit } from '@app/modules/audit/hooks/useStationsWithAudit';
import { auditReportsGetGipMetadataParamsForOrderQueryOptions } from '@mi-front/api-v2';
import { GipReportParametersForOrderIdResult } from '@mi-front/core';
import { useQueryClient } from '@tanstack/react-query';
import { useFormikContext } from 'formik';
import { useState } from 'react';
import { GipReportValues } from '../../types';

/** Loads GIP form values (stations, advertisers, campaigns, versions, dates) from order id(s). */
export function useLoadParamsForOrderId(orderIds: string[]) {
  const [isLoading, setLoading] = useState(false);
  const [loadedOrderParams, setLoadedOrderParams] =
    useState<GipReportParametersForOrderIdResult | null>(null);
  const fp = useFormikContext<GipReportValues>();
  const { notifyWarn } = useToast();
  const stations = useStationsWithAudit();
  const queryClient = useQueryClient();

  const loadOrderParams = async () => {
    setLoading(true);

    try {
      const params = await queryClient.fetchQuery(
        auditReportsGetGipMetadataParamsForOrderQueryOptions({ orderId: orderIds }),
      );

      if (!params.stationsIds.length) {
        notifyWarn('No se encontraron schedules asociados a la orden');
        return;
      }

      setLoadedOrderParams(params as GipReportParametersForOrderIdResult);

      if (fp.values.stationsSelectionType === 'advanced') {
        await fp.setFieldValue(
          'stationsFilter',
          stations
            .filter((s) => params.stationsIds.includes(s.id))
            .map<IndividualStationFilterOption>((s) => ({
              type: OptionType.IndividualStation,
              value: s,
              label: s.name,
            })),
        );
      } else {
        await fp.setFieldValue(
          'stations',
          stations.filter((s) => params.stationsIds.includes(s.id)),
        );
      }

      await fp.setFieldValue(
        'campaigns',
        params.campaigns.filter((v) => !!v).map((name) => ({ name })),
      );
      await fp.setFieldValue(
        'advertisers',
        params.advertisers.filter((v) => !!v).map((name) => ({ name })),
      );
      await fp.setFieldValue(
        'versions',
        params.versions.filter((v) => !!v).map((name) => ({ name })),
      );

      if (params.schedulesRange.from) {
        await fp.setFieldValue('from', params.schedulesRange.from);
      }

      if (params.schedulesRange.to) {
        await fp.setFieldValue('to', params.schedulesRange.to);
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    loadOrderParams,
    loadedOrderParams,
    isLoading,
  };
}
